(() => {
  if (customElements.get('taffstyle-color-select')) return;
  class TaffstyleColorSelect extends HTMLElement {
    connectedCallback() {
      queueMicrotask(() => this.initialize());
    }
    disconnectedCallback() {
      this.events?.abort();
      this.events = null;
      this.button?.remove();
      this.menu?.remove();
      this.removeAttribute('data-ready');
      this.removeAttribute('data-open');
      if (this.label && this.select) this.label.htmlFor = this.select.id;
    }
    initialize() {
      if (!this.isConnected || this.events) return;
      const select = this.querySelector('select');
      if (!select) return;
      this.select = select;
      this.events = new AbortController();
      const listen = (node, type, handler) => node.addEventListener(type, handler, { signal: this.events.signal });
      const label = select.labels?.[0];
      this.label = label;
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'ts-color-trigger';
      button.id = select.id + '-color-button';
      button.setAttribute('aria-expanded', 'false');
      button.setAttribute('aria-controls', select.id + '-color-menu');
      const selected = document.createElement('span');
      selected.className = 'ts-color-selected';
      button.append(selected);
      const chevron = document.createElement('span');
      chevron.className = 'ts-color-chevron';
      chevron.setAttribute('aria-hidden', 'true');
      button.append(chevron);
      const menu = document.createElement('span');
      menu.className = 'ts-color-menu';
      menu.id = select.id + '-color-menu';
      menu.setAttribute('role', 'group');
      menu.setAttribute('aria-label', this.dataset.label || label?.textContent.trim() || select.name);
      menu.hidden = true;
      this.button = button;
      this.menu = menu;
      this.append(button, menu);
      const renderValue = (target, option) => {
        target.replaceChildren();
        const text = document.createElement('span');
        text.className = 'ts-color-name';
        text.textContent = option.textContent.trim();
        if (this.dataset.swatches === 'false') {
          target.append(text);
          return;
        }
        const dot = document.createElement('span');
        dot.className = 'ts-color-dot';
        dot.setAttribute('aria-hidden', 'true');
        const colorClass = option.dataset.colorClass;
        if (colorClass) dot.classList.add(...colorClass.split(/\s+/).filter(Boolean));
        const swatchBackground = option.dataset.tsSwatchBackground;
        if (swatchBackground && CSS.supports('background', swatchBackground)) dot.style.setProperty('background', swatchBackground, 'important');
        else if (option.dataset.tsSwatchImage) {
          dot.style.backgroundImage = 'url(' + JSON.stringify(option.dataset.tsSwatchImage) + ')';
          if (option.dataset.tsVariantImage === 'true') dot.classList.add('ts-color-dot-variant');
        }
        else {
          const color = option.dataset.tsSwatchColor || option.dataset.color;
          if (color && CSS.supports('color', color)) dot.style.backgroundColor = color;
        }
        target.append(dot, text);
        let style = getComputedStyle(dot);
        if (style.backgroundImage === 'none' && style.backgroundColor === 'rgba(0, 0, 0, 0)' && option.dataset.colorFallbackClass && / - (Yellow|Light Blue|Orange|Purple|Red|Dark Pink|Light Pink|Brown|Ice White|Dark Blue|Black|Green|Dark Green|Ice Blue|White)$/.test(option.textContent.trim())) {
          dot.classList.add(option.dataset.colorFallbackClass);
          style = getComputedStyle(dot);
        }
        if (style.backgroundImage.includes('gradient(')) {
          dot.style.setProperty('background-repeat', 'no-repeat', 'important');
          dot.style.setProperty('background-origin', 'border-box', 'important');
        }
        if (style.backgroundImage === 'none' && (style.backgroundColor === 'rgba(0, 0, 0, 0)' || style.backgroundColor === 'transparent')) dot.hidden = true;
      };
      const sync = () => {
        const option = select.selectedOptions[0];
        if (!option) return;
        renderValue(selected, option);
        button.setAttribute('aria-label', (this.dataset.label || '') + ': ' + option.textContent.trim());
        button.disabled = select.disabled;
        [...menu.children].forEach((choice, index) => choice.setAttribute('aria-pressed', String(index === select.selectedIndex)));
      };
      [...select.options].forEach((option, index) => {
        const choice = document.createElement('button');
        choice.type = 'button';
        choice.className = 'ts-color-choice';
        choice.disabled = option.disabled;
        if (option.dataset.class === 'disabled') choice.classList.add('ts-color-unavailable');
        menu.append(choice);
        renderValue(choice, option);
        const check = document.createElement('span');
        check.className = 'ts-color-check';
        check.setAttribute('aria-hidden', 'true');
        check.textContent = '✓';
        choice.append(check);
        listen(choice, 'click', () => {
          const changed = select.selectedIndex !== index;
          select.selectedIndex = index;
          sync();
          this.close(true);
          if (changed) select.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });
      listen(button, 'click', () => menu.hidden ? this.open() : this.close(false));
      listen(select, 'change', sync);
      listen(select, 'invalid', () => button.focus());
      listen(document, 'pointerdown', event => { if (!this.contains(event.target)) this.close(false); });
      listen(this, 'focusout', event => { if (event.relatedTarget && !this.contains(event.relatedTarget)) this.close(false); });
      listen(this, 'keydown', event => {
        if (event.key === 'Escape') { event.preventDefault(); this.close(true); return; }
        if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        if (menu.hidden) this.open();
        const choices = [...menu.querySelectorAll('button:not(:disabled)')];
        if (!choices.length) return;
        const index = choices.indexOf(document.activeElement);
        const next = event.key === 'Home' ? 0 : event.key === 'End' ? choices.length - 1 : index === -1 ? (event.key === 'ArrowUp' ? choices.length - 1 : 0) : (index + (event.key === 'ArrowUp' ? -1 : 1) + choices.length) % choices.length;
        choices[next].focus();
      });
      if (label) {
        label.htmlFor = button.id;
      }
      sync();
      this.setAttribute('data-ready', '');
    }
    open() {
      document.querySelectorAll('taffstyle-color-select[data-open]').forEach(picker => {
        if (picker !== this) picker.close(false);
      });
      this.setAttribute('data-open', '');
      this.menu.hidden = false;
      this.button.setAttribute('aria-expanded', 'true');
      const selected = this.menu.children[this.select.selectedIndex];
      if (selected) this.menu.scrollTop = Math.max(0, selected.offsetTop - (this.menu.clientHeight - selected.offsetHeight) / 2);
    }
    close(focus) {
      if (!this.menu || !this.button) return;
      this.removeAttribute('data-open');
      this.menu.hidden = true;
      this.button.setAttribute('aria-expanded', 'false');
      if (focus) this.button.focus({ preventScroll: true });
    }
  }
  customElements.define('taffstyle-color-select', TaffstyleColorSelect);
})();
